import React from 'react';
import { Check, Minus, X as XIcon } from 'lucide-react';

const cell = (text) => {
  if (text === true) return <Check className="w-5 h-5 text-emerald-500 mx-auto" aria-label="Yes" />;
  if (text === false) return <XIcon className="w-5 h-5 text-slate-300 mx-auto" aria-label="No" />;
  return <span className="text-xs text-slate-600 leading-snug">{text}</span>;
};

const rows = [
  { feature: 'Delivery Methodology',
    sitePlatforms: 'Task-centric', design: 'Design & BIM focused', fm: 'Reactive / planned maintenance', publicSector: 'Compliance-driven',
    videra: { tag: 'Stage-gated, evidence-driven' } },
  { feature: 'Governance Framework',
    sitePlatforms: 'Limited', design: 'Design review', fm: 'Maintenance operations', publicSector: 'Regulatory compliance',
    videra: { tag: 'Embedded & unified' } },
  { feature: 'Assurance (Technical, Commercial, Safety, Quality)',
    sitePlatforms: 'Basic tracking', design: 'Design checks', fm: 'Service quality', publicSector: 'Audit trails',
    videra: { tag: 'Comprehensive & integrated' } },
  { feature: 'Commercial Control & Cost Assurance',
    sitePlatforms: 'Basic budget tracking', design: 'N/A', fm: 'N/A', publicSector: 'Financial reporting',
    videra: { tag: 'Advanced & governed' } },
  { feature: 'AI-Driven Insights & Risk Detection',
    sitePlatforms: 'Limited analytics', design: 'N/A', fm: 'Predictive maintenance', publicSector: 'Reporting',
    videra: { tag: 'Proactive & narrative' } },
  { feature: 'Evidence Packs & Auditability',
    sitePlatforms: 'Document storage', design: 'Design files', fm: 'Service records', publicSector: 'Compliance records',
    videra: { tag: 'Structured & automated' } },
  { feature: 'Portfolio-Wide Visibility',
    sitePlatforms: 'Project-specific', design: 'Design package view', fm: 'Asset performance', publicSector: 'Programmatic',
    videra: { tag: 'Holistic & real-time' } },
  { feature: 'Integration Layer for Ecosystem',
    sitePlatforms: 'Standalone', design: 'Standalone', fm: 'Standalone', publicSector: 'Standalone',
    videra: { tag: 'Core functionality' } },
  { feature: 'Operating System for Delivery',
    sitePlatforms: false, design: false, fm: false, publicSector: false,
    videra: { tag: 'Yes', strong: true } },
];

const headers = [
  { key: 'feature',        label: 'Capability',                       sub: '' },
  { key: 'sitePlatforms',  label: 'Construction-Site Platforms',     sub: 'Field-first apps' },
  { key: 'design',         label: 'Design-Led Platforms',            sub: 'BIM & coordination' },
  { key: 'fm',             label: 'FM / CAFM Systems',               sub: 'Maintenance & assets' },
  { key: 'publicSector',   label: 'Public-Sector Capital Tools',     sub: 'Reporting & compliance' },
  { key: 'videra',         label: 'Videra',                          sub: 'Operating System for Delivery', highlight: true },
];

export const ComparisonTable = () => (
  <div className="overflow-x-auto -mx-4 sm:mx-0" data-testid="v2-comparison-table">
    <div className="min-w-[920px] px-4 sm:px-0">
      <div className="grid grid-cols-6 gap-px bg-slate-200 border border-slate-200 rounded-xl overflow-hidden">
        {/* Header row */}
        {headers.map((h) => (
          <div key={h.key} className={`p-5 ${h.highlight ? 'bg-slate-900' : 'bg-white'}`}>
            <p className={`text-[10px] uppercase tracking-[0.18em] font-semibold ${h.highlight ? 'text-blue-400' : 'text-slate-500'}`}>
              {h.label}
            </p>
            {h.sub && <p className={`text-[11px] mt-1 ${h.highlight ? 'text-slate-400' : 'text-slate-400'}`}>{h.sub}</p>}
          </div>
        ))}

        {rows.map((r, i) => {
          const alt = i % 2 ? 'bg-slate-50' : 'bg-white';
          const altDark = i % 2 ? 'bg-slate-800' : 'bg-slate-900';
          return (
            <React.Fragment key={r.feature}>
              <div className={`${alt} p-5 text-sm font-medium text-slate-900`}>{r.feature}</div>
              <div className={`${alt} p-5 text-center`}>{cell(r.sitePlatforms)}</div>
              <div className={`${alt} p-5 text-center`}>{cell(r.design)}</div>
              <div className={`${alt} p-5 text-center`}>{cell(r.fm)}</div>
              <div className={`${alt} p-5 text-center`}>{cell(r.publicSector)}</div>
              <div className={`${altDark} p-5 text-center`}>
                {typeof r.videra === 'object' ? (
                  <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${r.videra.strong ? 'text-emerald-300' : 'text-blue-200'}`}>
                    {r.videra.strong && <Check className="w-4 h-4" />}
                    {r.videra.tag}
                  </span>
                ) : cell(r.videra)}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
    <p className="mt-4 text-xs text-slate-500 px-4 sm:px-0">
      <Check className="w-3.5 h-3.5 text-emerald-500 inline mr-1" /> Fully embedded · <Minus className="w-3.5 h-3.5 text-amber-500 inline mx-1" /> Partial · <XIcon className="w-3.5 h-3.5 text-slate-400 inline mx-1" /> Not present. Categories shown are generic.
    </p>
  </div>
);

export default ComparisonTable;
